import React, {PropsWithChildren, useEffect, useRef} from "react";
import {BreakpointOptions, BreakpointType, SwiperElementType, SwiperOptions} from "../../molecules/SwiperTypes";
import ListItemCard from "../productList/ListItemCard";
import {isCart, IUnsafeCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {AddToCartButton} from "../../cart/AddToCartButton/AddToCartButtonPortal";
import {IItemContent} from "../../../typescript-library";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "swiper-container": Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>, "className"> & {
        init: boolean;
        class?: string;
      };
      "swiper-slide": Omit<React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>, "className"> & {
        class?: string;
      };
    }
  }
}

const defaultBreakpointOptionsMobile: BreakpointOptions = {
  slidesPerView: 2.5,
  slidesOffsetBefore: 16,
  slidesOffsetAfter: 16,
};

const defaultBreakpointOptionsDesktop: BreakpointOptions = {
  slidesPerView: 4,
  slidesOffsetBefore: 0,
  slidesOffsetAfter: 0,
};

export const defaultBreakpoints: BreakpointType = {
  0: {...defaultBreakpointOptionsMobile},
  768: {
    ...defaultBreakpointOptionsMobile,
    slidesPerView: 3.5,
  },
  941: {...defaultBreakpointOptionsDesktop},
  1132: {...defaultBreakpointOptionsDesktop},
};

type SwiperBuilderPropsType = {
  title: string;
  breakpoints?: BreakpointType;
  link?: React.ReactNode;
};

/**
 * swiper-container builder für alle swiper, die zuerst in React aufgebaut werden
 * @param children
 * @param title
 * @param link
 * @param breakpoints
 * @constructor
 */
const SwiperBuilder: React.FC<PropsWithChildren<SwiperBuilderPropsType>> = ({
  children,
  title,
  link,
  breakpoints = defaultBreakpoints,
}) => {
  const swiperRef = useRef<SwiperElementType>(null);
  const prevSpanRef = useRef(null);
  const nextSpanRef = useRef(null);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.initialize && prevSpanRef.current && nextSpanRef.current) {
      swiperRef.current.parentElement?.classList.add("visible");

      const elementOptions: SwiperOptions = {
        spaceBetween: 16,
        mousewheel: {
          forceToAxis: true,
        },
        breakpoints,
        navigation: {
          nextEl: nextSpanRef.current,
          prevEl: prevSpanRef.current,
        },
      };
      Object.assign(swiperRef.current, elementOptions);

      swiperRef.current.initialize();
    }
  }, []);

  useEffect(() => {
    if (swiperRef.current?.swiper.realIndex !== 0) {
      swiperRef.current?.swiper.slideTo(0, 0, false);
    }
  }, [children]);

  return (
    <div className="swiper-cross-selling">
      <div className="container-title">
        <h2 className="swiper-cross-selling__title fill">{title}</h2>
        {link}
      </div>
      <swiper-container init={false} ref={swiperRef} class="swiper-cross-selling__container">
        {React.Children.map(children, (child, key) => {
          return (
            <swiper-slide key={key} class="swiper-slide-card">
              {child}
            </swiper-slide>
          );
        })}
      </swiper-container>
      <div className="swiper-btn-prev" ref={prevSpanRef}>
        <span className="icon icon-chevron-left icon-size--32">&nbsp;</span>
      </div>
      <div className="swiper-btn-next" ref={nextSpanRef}>
        <span className="icon icon-chevron-right icon-size--32">&nbsp;</span>
      </div>
    </div>
  );
};

export default SwiperBuilder;

type SwiperWithTitleAndItemsProps = SwiperBuilderPropsType & {
  context: IUnsafeCartContext;
  items: IItemContent[];
};

export const SwiperWithTitleAndItems: React.FC<SwiperWithTitleAndItemsProps> = ({
  breakpoints,
  context,
  items,
  link,
  title,
}) => {
  return (
    <SwiperBuilder title={title} breakpoints={breakpoints} link={link}>
      {items.map((item, key) => (
        <React.Fragment key={key}>
          <ListItemCard item={item} />
          {context !== null && isCart(context) && (
            <AddToCartButton itemId={item.itemId} isSwiperAddToCartButton={true} />
          )}
        </React.Fragment>
      ))}
    </SwiperBuilder>
  );
};
